import { FC, ReactNode, useState } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import DbIcon from "designs/DesignB/components/icons";
import { FLEET_SORTS, FleetSort } from "utils/fleetSort";

export interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

export interface FleetLayoutProps {
  eyebrow: string;
  title: string;
  /** Cars the filters leave; undefined while the fleet loads. */
  count?: number;
  sort: FleetSort;
  onSort: (sort: FleetSort) => void;
  /** The filter sidebar's content. */
  filters: ReactNode;
  chips: FilterChip[];
  onClearAll: () => void;
  children: ReactNode;
}

/**
 * The frame of a Design B vehicle page (1NOW-11175): the bold title with the result count and a
 * sort on the right, the filters in a left sidebar (behind a Filters button below lg), the
 * active filters as removable chips with Clear all above the grid. The grid, its loading state
 * and its empty state are the page's own and come in as children.
 */
const FleetLayout: FC<FleetLayoutProps> = ({
  eyebrow,
  title,
  count,
  sort,
  onSort,
  filters,
  chips,
  onClearAll,
  children,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="db-vehicles">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-4 sm:mb-8">
        <div className="min-w-0">
          <div className="db-eyebrow mb-2">{eyebrow}</div>
          <h1 className="db-h1">
            {title}
            {typeof count === "number" && (
              <span className="db-h1__count">
                {count} {count === 1 ? "car" : "cars"}
              </span>
            )}
          </h1>
        </div>

        {/* Filters and Sort are one row of two 44px buttons (1NOW-11214): the same height, so they
            line up at every width, and the Sort one never wraps: below 360px it drops its
            "Sort by" label and shows the current choice with the chevron (below 400px when the
            Filters button carries its count, which widens it), and when even that runs out of
            room the choice is cut with an ellipsis instead of breaking onto a second line. */}
        <div className="flex max-w-full items-center gap-2 sm:gap-3">
          <button
            type="button"
            className="db-btn db-btn--ghost db-btn--sm shrink-0 !min-h-[44px] lg:!hidden"
            aria-expanded={open}
            aria-controls="db-filters"
            onClick={() => setOpen((v) => !v)}
          >
            <DbIcon name="sliders" className="h-5 w-5" />
            Filters
            {chips.length > 0 && <span className="db-badge-n">{chips.length}</span>}
          </button>

          {/* Our own list, not the browser's: a native <select> opens the
              operating system's menu, which no tenant colour or radius reaches. */}
          <Listbox value={sort} onChange={(v: FleetSort) => onSort(v)}>
            <div className="db-sort">
              <ListboxButton className="db-select" aria-label="Sort vehicles">
                <span className={`db-select__label ${chips.length > 0 ? "max-[399px]:hidden" : "max-[359px]:hidden"}`}>
                  Sort by
                </span>
                <span className="db-select__value">{FLEET_SORTS.find((s) => s.value === sort)?.label}</span>
                <DbIcon name="chevronDown" className="db-select__chev" />
              </ListboxButton>
              <ListboxOptions className="db-select__menu">
                {FLEET_SORTS.map((s) => (
                  <ListboxOption key={s.value} value={s.value} className="db-select__option">
                    {s.label}
                    <DbIcon name="check" className="db-select__check" />
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>
        </div>
      </div>

      <div className="db-vehicles__grid">
        <aside id="db-filters" className={`db-vehicles__aside ${open ? "is-open" : ""}`} aria-label="Filters">
          {filters}
        </aside>

        <div className="min-w-0">
          {chips.length > 0 && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              {chips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className="db-chip"
                  onClick={chip.onRemove}
                  aria-label={`Remove filter ${chip.label}`}
                >
                  {chip.label}
                  <DbIcon name="close" className="h-3.5 w-3.5" />
                </button>
              ))}
              <button type="button" className="db-chip-clear" onClick={onClearAll}>
                Clear all
              </button>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default FleetLayout;
